import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addDepartmentGroups, getDepartmentGroups } from '@/api/onsiteLiveAdmin';
import { ApiError } from '@/api/client';
import { formatDate } from '@/lib/dateFormat';
import type { DepartmentGroupMembership } from '@/types/oscar';

const KIND_LABEL: Record<DepartmentGroupMembership['Kind'], string> = {
  OLSecurity: 'Security',
  OLSubscription: 'Subscription',
};

function state(g: DepartmentGroupMembership, startDate: string | null): { text: string; tone: string } {
  if (g.Member) return { text: 'Has it', tone: 'bg-navy/10 text-navy' };
  if (!g.GroupActive) return { text: 'Inactive in OnsiteLive', tone: 'bg-darkGrey/10 text-darkGrey' };
  if (g.Deferred) {
    return { text: startDate ? `Added on ${formatDate(startDate.slice(0, 10))}` : 'Added on the start date', tone: 'bg-darkGrey/10 text-darkGrey' };
  }
  return { text: 'Missing', tone: 'bg-orange/15 text-orange' };
}

/**
 * Person › OnsiteLive — the department's groups (Access Groups step 3). Shows
 * the OnsiteLive groups the person's department gets (Admin › Onboarding ›
 * Access Groups) against this account, and the Baseplan names as a checklist.
 * "Add the department's groups" adds the missing OnsiteLive ones and NEVER
 * removes anything; inactive groups are not added, and subscription groups
 * wait for an active account. New accounts get them automatically.
 */
export function DepartmentGroupsPanel({ userId, canWrite, startDate = null }: {
  userId: number;
  canWrite: boolean;
  /** The date a waiting login is switched on (OnsiteLiveUser.WaitingForStartDate) - dates the deferred tags. */
  startDate?: string | null;
}) {
  const qc = useQueryClient();
  const key = ['onsitelive-admin', 'user', userId, 'department-groups'] as const;
  const q = useQuery({ queryKey: key, queryFn: ({ signal }) => getDepartmentGroups(userId, signal) });
  const [done, setDone] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: () => addDepartmentGroups(userId),
    onSuccess: (result) => {
      qc.setQueryData(key, result.DepartmentGroups);
      // The memberships editor below reads the user record.
      void qc.invalidateQueries({ queryKey: ['onsitelive-admin', 'user', userId], exact: true });
      const n = result.SecurityGroupsAdded + result.SubscriptionGroupsAdded;
      setDone(n === 0 ? 'Nothing to add — the account already has them.' : `Added ${n} group${n === 1 ? '' : 's'}.`);
    },
  });

  if (q.isLoading) return <p className="text-sm text-darkGrey/70">Loading the department&rsquo;s groups…</p>;
  if (q.isError || !q.data) return <p className="text-sm text-red">Could not load the department&rsquo;s groups.</p>;

  const d = q.data;
  const missing = d.Groups.filter((g) => !g.Member && g.GroupActive && !g.Deferred).length;

  return (
    <section className="rounded border border-border bg-white p-4" aria-label="Department groups">
      <h3 className="font-heading text-base font-bold text-navy">Department groups</h3>
      {d.Reason ? (
        <p className="mt-1 text-sm text-darkGrey/80">{d.Reason}</p>
      ) : (
        <>
          <p className="mt-1 text-sm text-darkGrey/80">
            What <span className="font-mono font-semibold text-navy">{d.DepartmentCode}</span>
            {d.DepartmentDescription && <> {d.DepartmentDescription}</>} gets, set in{' '}
            <Link to="/onboarding/access-groups" className="text-navy hover:underline">Access Groups</Link>.
            New accounts get these automatically.
          </p>
          {d.AppliedDepartmentCode && d.AppliedDepartmentCode !== d.DepartmentCode && (
            <p className="mt-2 rounded border border-orange/40 bg-orange/5 px-3 py-2 text-sm text-darkGrey">
              The department changed from <span className="font-mono font-semibold">{d.AppliedDepartmentCode}</span>. Within
              15 minutes the old department&rsquo;s groups that this one doesn&rsquo;t get are removed and this one&rsquo;s are
              added, automatically.
            </p>
          )}

          {d.Groups.length === 0 ? (
            <p className="mt-2 text-sm text-darkGrey/70">This department gets no OnsiteLive groups.</p>
          ) : (
            <ul className="mt-3 space-y-1.5" aria-label="OnsiteLive groups">
              {d.Groups.map((g) => {
                const s = state(g, startDate);
                return (
                  <li key={`${g.Kind}-${g.GroupId}`} className="flex items-center gap-2 rounded border border-border px-2 py-1 text-sm">
                    <span className="font-semibold text-navy">{g.Name ?? `Group ${g.GroupId}`}</span>
                    <span className="text-xs text-darkGrey/70">{KIND_LABEL[g.Kind]}</span>
                    <span className={`ml-auto shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${s.tone}`}>{s.text}</span>
                  </li>
                );
              })}
            </ul>
          )}

          {d.BaseplanGroups.length > 0 && (
            <div className="mt-3">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-darkGrey/70">Baseplan — set up by hand</h4>
              <ul className="mt-1 flex flex-wrap gap-1.5" aria-label="Baseplan groups">
                {d.BaseplanGroups.map((n) => (
                  <li key={n} className="rounded border border-border bg-bg/50 px-2 py-0.5 text-xs text-darkGrey">{n}</li>
                ))}
              </ul>
            </div>
          )}

          {canWrite && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <button
                type="button"
                disabled={missing === 0 || add.isPending}
                onClick={() => { setDone(null); add.mutate(); }}
                className="rounded bg-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-navy/90 disabled:opacity-50"
              >
                {add.isPending ? 'Adding…' : `Add the department's groups${missing > 0 ? ` (${missing})` : ''}`}
              </button>
              <span className="text-xs text-darkGrey/70">Adds what is missing; never removes a group.</span>
            </div>
          )}
          {done && <p role="status" className="mt-2 text-sm text-navy">{done}</p>}
          {add.isError && (
            <p role="alert" className="mt-2 text-sm text-red">
              {add.error instanceof ApiError ? add.error.message : 'Could not add the groups.'}
            </p>
          )}
        </>
      )}
    </section>
  );
}
